import type { FastifyRequest, FastifyReply } from 'fastify'
import type { Firestore } from 'firebase-admin/firestore'
import type { 
  CreateClanDTO, 
  UpdateClanDTO, 
  SendMessageDTO, 
  CreatePostDTO, 
  CreateClanCommentDTO,
  UpdatePresenceDTO 
} from '@my-roadtrip/types'
import { ClanService } from './clan.service.js'
import { ApiResponse } from '../../utils/response'

export class ClanController {
  private readonly service: ClanService

  constructor(firestore: Firestore) {
    this.service = new ClanService(firestore)
  }

  // ========================================
  // CLAN MANAGEMENT
  // ========================================

  async createClan(
    request: FastifyRequest<{ Body: CreateClanDTO }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const userProfile = {
        displayName: request.user?.name || request.user?.email || 'User',
        photoURL: request.user?.picture
      }

      const clan = await this.service.createClan(userId, request.body, userProfile)
      return reply.send(clan)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to create clan')
    }
  }

  async getClan(
    request: FastifyRequest<{ Params: { clanId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const clan = await this.service.getClan(request.params.clanId)
      if (!clan) return ApiResponse.notFound(reply, 'Clan not found')
      
      return reply.send(clan)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get clan')
    }
  }

  async updateClan(
    request: FastifyRequest<{ Params: { clanId: string }; Body: UpdateClanDTO }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.updateClan(request.params.clanId, userId, request.body)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to update clan')
    }
  }

  async deleteClan(
    request: FastifyRequest<{ Params: { clanId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.deleteClan(request.params.clanId, userId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to delete clan')
    }
  }

  async getUserClans(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const clans = await this.service.getUserClans(userId)
      return reply.send(clans)
    } catch (error) {
      request.log.error({ err: error }, 'Failed to get user clans, returning empty list')
      return reply.send([])
    }
  }

  async searchClans(
    request: FastifyRequest<{ Querystring: { q: string } }>,
    reply: FastifyReply
  ) {
    try {
      const query = request.query.q
      if (!query) return ApiResponse.badRequest(reply, 'Search query is required')

      const clans = await this.service.searchClans(query)
      return reply.send(clans)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to search clans')
    }
  }

  // ========================================
  // MEMBER MANAGEMENT
  // ========================================

  async joinClan(
    request: FastifyRequest<{ Params: { clanId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const userProfile = {
        displayName: request.user?.name || request.user?.email || 'User',
        photoURL: request.user?.picture
      }

      await this.service.joinClan(request.params.clanId, userId, userProfile)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to join clan')
    }
  }

  async leaveClan(
    request: FastifyRequest<{ Params: { clanId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.leaveClan(request.params.clanId, userId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to leave clan')
    }
  }

  async kickMember(
    request: FastifyRequest<{ Params: { clanId: string; userId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const requesterId = request.user?.uid
      if (!requesterId) return ApiResponse.unauthorized(reply)

      await this.service.kickMember(request.params.clanId, requesterId, request.params.userId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to kick member')
    }
  }

  async updateMemberRole(
    request: FastifyRequest<{ 
      Params: { clanId: string; userId: string }
      Body: { role: 'admin' | 'member' }
    }>,
    reply: FastifyReply
  ) {
    try {
      const requesterId = request.user?.uid
      if (!requesterId) return ApiResponse.unauthorized(reply)

      await this.service.updateMemberRole(
        request.params.clanId, 
        requesterId, 
        request.params.userId, 
        request.body.role
      )
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to update member role')
    }
  }

  // ========================================
  // CHAT MESSAGES
  // ========================================

  async sendMessage(
    request: FastifyRequest<{ Params: { clanId: string }; Body: SendMessageDTO }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const userProfile = {
        displayName: request.user?.name || request.user?.email || 'User',
        photoURL: request.user?.picture
      }

      const message = await this.service.sendMessage(
        request.params.clanId, 
        userId, 
        userProfile, 
        request.body
      )
      return reply.send(message)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to send message')
    }
  }

  async getMessages(
    request: FastifyRequest<{ Params: { clanId: string }; Querystring: { limit?: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const limit = request.query.limit ? Number.parseInt(request.query.limit) : 50

      const messages = await this.service.getMessages(request.params.clanId, userId, limit)
      return reply.send(messages)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get messages')
    }
  }

  async deleteMessage(
    request: FastifyRequest<{ Params: { clanId: string; messageId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.deleteMessage(request.params.clanId, request.params.messageId, userId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to delete message')
    }
  }

  // ========================================
  // FEED POSTS
  // ========================================

  async createPost(
    request: FastifyRequest<{ Params: { clanId: string }; Body: CreatePostDTO }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const userProfile = {
        displayName: request.user?.name || request.user?.email || 'User',
        photoURL: request.user?.picture
      }

      const post = await this.service.createPost(
        request.params.clanId, 
        userId, 
        userProfile, 
        request.body
      )
      return reply.send(post)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to create post')
    }
  }

  async getPosts(
    request: FastifyRequest<{ Params: { clanId: string }; Querystring: { limit?: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const limit = request.query.limit ? Number.parseInt(request.query.limit) : 20

      const posts = await this.service.getPosts(request.params.clanId, userId, limit)
      return reply.send(posts)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get posts')
    }
  }

  async likePost(
    request: FastifyRequest<{ Params: { clanId: string; postId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.likePost(request.params.clanId, request.params.postId, userId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to like post')
    }
  }

  async deletePost(
    request: FastifyRequest<{ Params: { clanId: string; postId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.deletePost(request.params.clanId, request.params.postId, userId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to delete post')
    }
  }

  // ========================================
  // COMMENTS
  // ========================================

  async createComment(
    request: FastifyRequest<{ 
      Params: { clanId: string; postId: string }
      Body: CreateClanCommentDTO 
    }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const userProfile = {
        displayName: request.user?.name || request.user?.email || 'User',
        photoURL: request.user?.picture
      }

      const comment = await this.service.createComment(
        request.params.clanId,
        request.params.postId,
        userId,
        userProfile,
        request.body
      )
      return reply.send(comment)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to create comment')
    }
  }

  async getComments(
    request: FastifyRequest<{ Params: { clanId: string; postId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const comments = await this.service.getComments(
        request.params.clanId,
        request.params.postId,
        userId
      )
      return reply.send(comments)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get comments')
    }
  }

  async deleteComment(
    request: FastifyRequest<{ Params: { clanId: string; postId: string; commentId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.deleteComment(
        request.params.clanId,
        request.params.postId,
        request.params.commentId,
        userId
      )
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to delete comment')
    }
  }

  // ========================================
  // INVITES
  // ========================================

  async inviteUser(
    request: FastifyRequest<{ Params: { clanId: string }; Body: { userId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const inviterId = request.user?.uid
      if (!inviterId) return ApiResponse.unauthorized(reply)

      const invite = await this.service.inviteUser(
        request.params.clanId,
        inviterId,
        request.body.userId
      )
      return reply.send(invite)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to invite user')
    }
  }

  async acceptInvite(
    request: FastifyRequest<{ Params: { inviteId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const userProfile = {
        displayName: request.user?.name || request.user?.email || 'User',
        photoURL: request.user?.picture
      }

      await this.service.acceptInvite(request.params.inviteId, userId, userProfile)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to accept invite')
    }
  }

  async declineInvite(
    request: FastifyRequest<{ Params: { inviteId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.declineInvite(request.params.inviteId, userId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to decline invite')
    }
  }

  async getUserInvites(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const invites = await this.service.getUserInvites(userId)
      return reply.send(invites)
    } catch (error) {
      request.log.error({ err: error }, 'Failed to get user invites, returning empty list')
      return reply.send([])
    }
  }

  // ========================================
  // PRESENCE
  // ========================================

  async updatePresence(
    request: FastifyRequest<{ Params: { clanId: string }; Body: UpdatePresenceDTO }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      await this.service.updatePresence(request.params.clanId, userId, request.body)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to update presence')
    }
  }

  async getOnlineMembers(
    request: FastifyRequest<{ Params: { clanId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) return ApiResponse.unauthorized(reply)

      const members = await this.service.getOnlineMembers(request.params.clanId, userId)
      return reply.send(members)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get online members')
    }
  }
}
